import { BoostStatus, BOOST_DURATION_MINUTES, BOOST_MULTIPLIER } from '../value-objects/BoostStatus';

export interface XPBoostProps {
  id: string;
  studentId: string;
  activatedAt: Date;
  expiresAt: Date;
  isActive: boolean;
  xpEarned: number;
  createdAt: Date;
}

export class XPBoost {
  private readonly props: XPBoostProps;

  constructor(props: XPBoostProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get studentId(): string {
    return this.props.studentId;
  }

  get activatedAt(): Date {
    return this.props.activatedAt;
  }

  get expiresAt(): Date {
    return this.props.expiresAt;
  }

  get xpEarned(): number {
    return this.props.xpEarned;
  }

  /**
   * Check if boost is currently active
   */
  isActive(): boolean {
    return this.props.isActive && new Date() < this.props.expiresAt;
  }

  /**
   * Check if boost has expired
   */
  hasExpired(): boolean {
    return new Date() >= this.props.expiresAt;
  }

  /**
   * Get current status
   */
  getStatus(): BoostStatus {
    if (this.isActive()) return 'ACTIVE';
    if (this.hasExpired()) return 'EXPIRED';
    return 'AVAILABLE';
  }

  /**
   * Get remaining time in seconds
   */
  getRemainingSeconds(): number {
    if (this.hasExpired()) return 0;
    return Math.floor((this.props.expiresAt.getTime() - new Date().getTime()) / 1000);
  }

  /**
   * Get remaining time in minutes
   */
  getRemainingMinutes(): number {
    return Math.ceil(this.getRemainingSeconds() / 60);
  }

  /**
   * Get remaining time formatted as MM:SS
   */
  getFormattedTimeRemaining(): string {
    const totalSeconds = this.getRemainingSeconds();
    if (totalSeconds <= 0) return '0:00';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Get progress percentage (0-100, where 100 = time is up)
   */
  getProgressPercentage(): number {
    const duration = BOOST_DURATION_MINUTES * 60;
    const remaining = this.getRemainingSeconds();
    const elapsed = duration - remaining;
    return Math.min(100, Math.round((elapsed / duration) * 100));
  }

  /**
   * Get the multiplier value
   */
  getMultiplier(): number {
    return this.isActive() ? BOOST_MULTIPLIER : 1.0;
  }

  /**
   * Get duration in minutes
   */
  static getDurationMinutes(): number {
    return BOOST_DURATION_MINUTES;
  }

  /**
   * Create a new boost starting now
   */
  static createNew(studentId: string): Omit<XPBoostProps, 'id' | 'createdAt'> {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + BOOST_DURATION_MINUTES * 60 * 1000);
    return {
      studentId,
      activatedAt: now,
      expiresAt,
      isActive: true,
      xpEarned: 0,
    };
  }

  toPlainObject(): XPBoostProps {
    return { ...this.props };
  }
}
